/**
 * Contratos compartilhados entre a API (NestJS) e o frontend (React).
 * Este pacote não tem dependências de runtime de propósito: ele é importado
 * pelos dois lados e precisa funcionar tanto em Node quanto no browser.
 */

// ─────────────────────────────────────────────────────────────
// Identidade / sessão
// ─────────────────────────────────────────────────────────────

export type UserRole = 'admin' | 'agent' | 'viewer';

export interface SessionUser {
  id: string;
  externalId: string;
  name: string;
  email: string | null;
  role: UserRole;
  /**
   * Quando a foto deste atendente mudou pela última vez; `null` se ele não tem
   * foto.
   *
   * O painel usa isto como versão na URL da foto. Sem ela, a foto trocada
   * continuaria mostrando a antiga até o navegador resolver reconsultar — e
   * ninguém entende por que a própria foto "não mudou".
   */
  avatarUpdatedAt: string | null;
  /**
   * Se este usuário pode excluir tickets em definitivo.
   *
   * Não sai do papel (`role`): a exclusão é nominal, liberada por uma lista de
   * ids no servidor (`TICKET_DELETE_ALLOWED_USER_IDS`). Vem na sessão para que o
   * painel não ofereça um botão que a API vai recusar — mas quem decide é sempre
   * o servidor, que refaz a checagem em cada exclusão.
   */
  canDeleteTickets: boolean;
}

export interface SessionTenant {
  id: string;
  externalId: string;
  name: string;
}

export interface SessionResponse {
  token: string;
  expiresAt: string;
  user: SessionUser;
  tenant: SessionTenant;
}

// ─────────────────────────────────────────────────────────────
// WhatsApp
// ─────────────────────────────────────────────────────────────

export type WhatsappStatus =
  | 'disconnected'
  | 'connecting'
  | 'qr_required'
  | 'connected'
  | 'logged_out'
  | 'error';

/** Estado da integração de voz (Wavoip) desta sessão. */
export type WavoipStatus = 'not_configured' | 'connecting' | 'connected' | 'error';

export interface WhatsappSession {
  id: string;
  name: string;
  phoneNumber: string | null;
  status: WhatsappStatus;
  qrCode: string | null;
  lastError: string | null;
  connectedAt: string | null;
  /** Nunca expõe o token em si — apenas se há um configurado. */
  wavoipConfigured: boolean;
  wavoipStatus: WavoipStatus;
  /** URL a cadastrar no painel do Wavoip; só para administradores. */
  wavoipWebhookUrl: string | null;
  createdAt: string;
}

// ─────────────────────────────────────────────────────────────
// Chamadas de voz
// ─────────────────────────────────────────────────────────────

export type CallDirection = 'inbound' | 'outbound';
export type CallStatus = 'ringing' | 'active' | 'ended' | 'missed' | 'rejected' | 'failed';

export interface Call {
  id: string;
  conversationId: string | null;
  direction: CallDirection;
  status: CallStatus;
  peerPhone: string;
  peerName: string | null;
  answeredByUserId: string | null;
  answeredByName: string | null;
  startedAt: string;
  answeredAt: string | null;
  endedAt: string | null;
  durationSeconds: number;
  recordUrl: string | null;
  endReason: string | null;
}

/**
 * Resposta que decide se ESTE atendente deve ser chamado por uma ligação.
 *
 * O SDK entrega a oferta de chamada a todo painel conectado ao mesmo número —
 * não há como filtrar do lado do provedor. Quem decide é o servidor, que
 * conhece o responsável pela conversa.
 */
export interface CallRoutingDecision {
  shouldRing: boolean;
  reason: 'assigned_to_you' | 'unassigned' | 'assigned_to_other' | 'read_only' | 'no_conversation';
  conversationId: string | null;
  contactId: string | null;
  contactName: string | null;
  /** Versão da foto do contato; a barra de chamada mostra quem está ligando. */
  contactAvatarUpdatedAt: string | null;
  assignedToName: string | null;
}

// ─────────────────────────────────────────────────────────────
// Conversas e mensagens
// ─────────────────────────────────────────────────────────────

export type ConversationStatus = 'open' | 'pending_human' | 'closed';
export type ConversationHandler = 'bot' | 'human';
export type MessageDirection = 'inbound' | 'outbound';
export type MessageSender = 'contact' | 'bot' | 'agent' | 'system';
export type MessageType = 'text' | 'image' | 'audio' | 'video' | 'document' | 'sticker' | 'other';

export interface Contact {
  id: string;
  waJid: string;
  phone: string | null;
  name: string | null;
  pushName: string | null;
  /**
   * Quando a foto do WhatsApp deste contato foi guardada; `null` se ele não tem
   * foto, ou a esconde de quem não está na agenda dele.
   *
   * Serve de versão para o cache do painel: mesma regra do avatar do atendente.
   */
  avatarUpdatedAt: string | null;
}

/**
 * Estado da transcrição de um áudio recebido. `null` para tudo que não é
 * áudio; `pending` enquanto a fila não processou.
 */
export type TranscriptionStatus = 'pending' | 'done' | 'failed' | 'skipped';

/**
 * O que aconteceu com a mensagem depois que o painel a registrou.
 *
 * Registrar e entregar são dois passos: a mensagem é gravada na hora e o envio
 * ao WhatsApp acontece na fila. Sem este campo, uma sessão fora do ar produz
 * bolha idêntica à de uma mensagem entregue — o atendente acha que respondeu, o
 * cliente nunca recebeu, e ninguém descobre até o cliente reclamar.
 *
 * `null` no que nunca vai ao WhatsApp: mensagem recebida e nota de sistema.
 */
export type MessageDelivery = 'pending' | 'sent' | 'failed';

export interface Message {
  id: string;
  conversationId: string;
  direction: MessageDirection;
  sender: MessageSender;
  type: MessageType;
  content: string;
  senderName: string | null;
  /**
   * Atendente que escreveu, quando foi gente. É por ele que a bolha acha a foto
   * — o nome não serve de chave: dois atendentes podem se chamar Ana.
   */
  senderUserId: string | null;
  mediaMimeType: string | null;
  waMessageId: string | null;
  /** Preenchido apenas em mensagens de áudio. */
  transcriptionStatus: TranscriptionStatus | null;
  /** Duração do áudio em segundos, quando o WhatsApp informa. */
  audioDurationSeconds: number | null;
  /**
   * Mensagem de visualização única (foto, vídeo ou áudio que "abre uma vez").
   *
   * O WhatsApp não entrega esse conteúdo a dispositivo pareado: no painel ela
   * chega vazia, e sem esta marca virava uma bolha "sem texto" que parecia falha
   * do sistema. Marcada, o painel pede ao atendente que solicite o reenvio.
   */
  viewOnce: boolean;
  /**
   * Há arquivo guardado no servidor para esta mensagem.
   *
   * O conteúdo é buscado em `GET /media/:messageId` — nunca por URL direta ao
   * arquivo, para o acesso passar pela checagem de empresa.
   */
  hasMedia: boolean;
  mediaBytes: number | null;
  /**
   * Nome original do arquivo, no que o painel enviou.
   *
   * É o que o cliente vê no cartão do documento no WhatsApp, e é o nome com que o
   * arquivo é baixado de volta no painel — o do disco não serve, porque lá ele é
   * o id da mensagem. `null` em tudo que não é anexo enviado daqui.
   */
  mediaFileName: string | null;
  /** Preenchido quando o arquivo foi apagado por retenção. */
  mediaRemovedAt: string | null;
  delivery: MessageDelivery | null;
  /** Por que a entrega falhou; o texto vem do WhatsApp ou da fila. */
  deliveryError: string | null;
  /** Mensagem a que esta responde, como estava quando a resposta foi escrita. */
  quoted: QuotedMessage | null;
  /** Última edição — feita no painel ou pelo cliente no WhatsApp dele. */
  editedAt: string | null;
  /**
   * Apagada para todos, no painel ou pelo cliente.
   *
   * O texto continua em `content`: o histórico é da empresa, e o atendente que
   * chega depois precisa saber o que foi dito e desdito. No WhatsApp do cliente
   * ela já não existe.
   */
  deletedAt: string | null;
  /**
   * A edição ou a exclusão pedida no painel não chegou ao WhatsApp.
   *
   * Separado de `deliveryError` porque a mensagem em si foi entregue: o que falhou
   * foi a mudança, e o cliente continua vendo a versão anterior.
   */
  changeError: string | null;
  createdAt: string;
}

/**
 * O pedaço da mensagem citada que a resposta carrega.
 *
 * Copiado na hora da resposta, e não buscado depois: a original pode ser editada,
 * apagada ou nem estar no painel (cliente respondendo a algo de antes do
 * pareamento), e a citação tem de continuar dizendo a que se respondia.
 */
export interface QuotedMessage {
  /** Id no painel, quando a original está nele; é o que permite rolar até ela. */
  messageId: string | null;
  sender: MessageSender;
  senderName: string | null;
  type: MessageType;
  /** Texto curto da original — a prévia, não a mensagem inteira. */
  preview: string;
}

/**
 * Prazo do WhatsApp para editar uma mensagem enviada: 15 minutos.
 *
 * Passado o prazo, o WhatsApp aceita o pedido e não faz nada — o painel mostraria
 * o texto novo e o cliente continuaria lendo o antigo. Por isso a regra vive aqui,
 * conferida dos dois lados.
 */
export const MESSAGE_EDIT_WINDOW_MS = 15 * 60 * 1000;

/**
 * Prazo para apagar para todos. O WhatsApp aceita por cerca de dois dias e meio;
 * dois dias deixam folga para o relógio do servidor e a fila.
 */
export const MESSAGE_DELETE_WINDOW_MS = 48 * 60 * 60 * 1000;

export interface MessageActor {
  userId: string;
  role: UserRole;
}

/**
 * Mensagem que o painel mandou e que ainda está no WhatsApp do cliente.
 *
 * Só o que saiu daqui pode ser editado ou apagado: a mensagem do cliente é do
 * aparelho dele, e o WhatsApp não deixa mexer nela.
 */
function isOwnDeliveredMessage(message: Message): boolean {
  return (
    message.direction === 'outbound' &&
    (message.sender === 'agent' || message.sender === 'bot') &&
    message.delivery === 'sent' &&
    !!message.waMessageId &&
    !message.deletedAt
  );
}

/**
 * Quem pode mexer numa mensagem já enviada: quem a escreveu, ou um administrador.
 * A do agente é de todos os atendentes — é deles que o cliente espera o conserto.
 */
function isAuthorOrAdmin(message: Message, actor: MessageActor): boolean {
  if (actor.role === 'viewer') return false;
  if (actor.role === 'admin' || message.sender === 'bot') return true;
  return message.senderUserId === actor.userId;
}

/** Mensagem do painel que ainda pode ter o texto trocado no WhatsApp do cliente. */
export function canEditMessage(message: Message, actor: MessageActor, now = Date.now()): boolean {
  return (
    isOwnDeliveredMessage(message) &&
    isAuthorOrAdmin(message, actor) &&
    // Só texto. A legenda de foto também é editável no WhatsApp, mas a edição que
    // o Baileys monta é de texto, e o que o aparelho do cliente faz com ela numa
    // foto não é garantido — o painel mostraria a legenda nova e o cliente, a velha.
    message.type === 'text' &&
    now - Date.parse(message.createdAt) < MESSAGE_EDIT_WINDOW_MS
  );
}

/**
 * Mensagem do painel que ainda pode ser apagada para todos.
 *
 * A que nunca saiu — falhou na fila — também pode: apagá-la é só tirá-la do
 * painel, porque o cliente não tem o que perder.
 */
export function canDeleteMessage(message: Message, actor: MessageActor, now = Date.now()): boolean {
  if (!isAuthorOrAdmin(message, actor) || message.deletedAt) return false;
  if (message.direction !== 'outbound' || message.sender === 'system') return false;
  if (message.delivery === 'failed') return true;
  return isOwnDeliveredMessage(message) && now - Date.parse(message.createdAt) < MESSAGE_DELETE_WINDOW_MS;
}

/**
 * Acesso remoto ao computador do cliente.
 *
 * A senha nunca vem aqui — só a informação de que existe uma guardada. Quem
 * precisa dela pede explicitamente, e esse pedido é registrado no log.
 */
export interface RemoteAccess {
  rustdeskId: string | null;
  hasPassword: boolean;
  note: string | null;
  updatedAt: string | null;
  /** Link que abre o RustDesk já no computador do cliente. */
  connectUri: string | null;
  /** Falso quando o servidor não tem chave de cifra configurada. */
  canStorePassword: boolean;
}

/**
 * Um atendimento — e, desde a numeração de tickets, o único que aquele contato
 * tem. Encerrar não fecha a linha: marca `closed`, e a próxima mensagem do
 * cliente reabre esta mesma conversa, com todo o histórico anterior no lugar.
 */
export interface Conversation {
  id: string;
  /** Número curto do ticket, o que se fala em voz alta ("o 142"). */
  ticketNumber: number;
  sessionId: string;
  contact: Contact;
  status: ConversationStatus;
  handler: ConversationHandler;
  assignedUserId: string | null;
  /** Nome de quem está com o atendimento; evita uma busca de usuário no painel. */
  assignedUserName: string | null;
  subject: string | null;
  summary: string | null;
  unreadCount: number;
  lastMessagePreview: string | null;
  lastMessageAt: string | null;
  escalationReason: string | null;
  /** Quantas vezes o cliente voltou depois de encerrado. */
  reopenCount: number;
  lastReopenedAt: string | null;
  closedAt: string | null;
  createdAt: string;
}

export interface ConversationDetail extends Conversation {
  messages: Message[];
}

// ─────────────────────────────────────────────────────────────
// Base de conhecimento
// ─────────────────────────────────────────────────────────────

export type KnowledgeSource = 'csv' | 'conversation' | 'manual';
export type EmbeddingStatus = 'pending' | 'processing' | 'ready' | 'failed';

export interface KnowledgeDocument {
  id: string;
  source: KnowledgeSource;
  sourceRef: string | null;
  title: string;
  question: string | null;
  answer: string | null;
  content: string;
  category: string | null;
  tags: string[];
  active: boolean;
  embeddingStatus: EmbeddingStatus;
  chunkCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface KnowledgeSearchHit {
  documentId: string;
  chunkId: string;
  title: string;
  content: string;
  category: string | null;
  source: KnowledgeSource;
  score: number;
  vectorScore: number | null;
  textScore: number | null;
}

export interface CsvImportJob {
  id: string;
  filename: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  totalRows: number;
  importedRows: number;
  failedRows: number;
  errors: CsvImportError[];
  createdAt: string;
  finishedAt: string | null;
}

export interface CsvImportError {
  line: number;
  message: string;
  raw?: string;
}

// ─────────────────────────────────────────────────────────────
// Formato do CSV de importação da base de conhecimento
// ─────────────────────────────────────────────────────────────

/**
 * Colunas canônicas aceitas no CSV. O cabeçalho é normalizado
 * (minúsculas, sem acentos) e os aliases abaixo são aceitos, de forma
 * que tanto planilhas em português quanto em inglês funcionem.
 *
 * Linhas em modo "pergunta/resposta" (FAQ): `pergunta` + `resposta`.
 * Linhas em modo "documento": `titulo` + `conteudo`.
 */
export const CSV_COLUMN_ALIASES: Record<string, string[]> = {
  question: ['pergunta', 'question', 'duvida', 'titulo_pergunta'],
  answer: ['resposta', 'answer', 'solucao'],
  title: ['titulo', 'title', 'assunto'],
  content: ['conteudo', 'content', 'texto', 'body'],
  category: ['categoria', 'category', 'setor', 'departamento'],
  tags: ['tags', 'etiquetas', 'palavras_chave', 'keywords'],
  externalId: ['id', 'id_externo', 'external_id', 'codigo'],
};

/** Cabeçalho recomendado, na ordem sugerida, para o arquivo de exemplo. */
export const CSV_RECOMMENDED_HEADER = [
  'pergunta',
  'resposta',
  'categoria',
  'tags',
  'id_externo',
] as const;

export interface KnowledgeCsvRow {
  question?: string;
  answer?: string;
  title?: string;
  content?: string;
  category?: string;
  tags?: string;
  externalId?: string;
}

// ─────────────────────────────────────────────────────────────
// Agente
// ─────────────────────────────────────────────────────────────

/**
 * `clarifying` é resposta enviada, como `replied`, mas contada à parte: é o
 * agente perguntando para poder responder, e é o que permite limitar quantas
 * perguntas seguidas ele faz antes de chamar um humano.
 */
export type AgentAction =
  | 'replied'
  | 'clarifying'
  /** Respondeu a uma saudação, agradecimento ou despedida — sem consultar a base. */
  | 'courtesy'
  | 'escalated'
  | 'skipped'
  | 'failed';

/**
 * Respostas de cortesia de fábrica.
 *
 * A de abertura pede o problema na mesma frase: quem cumprimenta está esperando
 * espaço para falar, e devolver só "olá" faz o cliente cumprimentar de novo.
 */
export const DEFAULT_GREETING_MESSAGE =
  'Olá! Sou o atendente virtual. Me conta o que está acontecendo que eu já começo a ajudar.';

export const DEFAULT_FAREWELL_MESSAGE =
  'Por nada! Se precisar de mais alguma coisa, é só escrever aqui.';

export interface AgentSettings {
  enabled: boolean;
  model: string;
  temperature: number;
  systemPrompt: string;
  minConfidence: number;
  maxContextMessages: number;
  ragTopK: number;
  handoffKeywords: string[];
  fallbackMessage: string;
  escalationMessage: string;
  /**
   * O que responder a uma saudação e a um agradecimento.
   *
   * Existem porque nenhuma das duas é dúvida: antes, a conversa que começava com
   * "bom dia" era transferida para uma pessoa antes de o cliente dizer o que
   * queria. Vazio faz o agente usar o texto de fábrica, em vez de mandar mensagem
   * em branco.
   */
  greetingMessage: string;
  farewellMessage: string;
  answerOnlyFromKnowledge: boolean;
  updatedAt: string;
}

export interface AgentRun {
  id: string;
  conversationId: string;
  action: AgentAction;
  confidence: number | null;
  reply: string | null;
  model: string;
  reason: string | null;
  /** O que a busca devolveu ao modelo: diz se a recuperação funcionou. */
  retrievedDocumentIds: string[];
  /** O que o modelo citou: diz se a resposta tinha apoio na base. */
  usedDocumentIds: string[];
  promptTokens: number | null;
  completionTokens: number | null;
  latencyMs: number | null;
  createdAt: string;
}

/** Saída estruturada que o modelo é obrigado a produzir. */
export interface AgentDecision {
  reply: string;
  confidence: number;
  needs_human: boolean;
  /**
   * `reply` é uma pergunta para descobrir qual caso da base se aplica.
   *
   * Separa "não sei" de "ainda não sei o suficiente para escolher": uma pergunta
   * não afirma nada, então não precisa de item da base que a sustente.
   */
  needs_clarification: boolean;
  reason: string;
  used_knowledge_ids: string[];
}

// ─────────────────────────────────────────────────────────────
// Webhooks de saída (atendimento -> sistema existente)
// ─────────────────────────────────────────────────────────────

/**
 * Nomes dos eventos enviados ao sistema existente.
 *
 * Ficam aqui, e não só na API, porque o painel também os usa — no filtro da tela
 * de entregas e nos rótulos. Um nome escrito duas vezes é um nome que divergirá.
 * O formato dos payloads continua sendo assunto da API (`src/webhooks/`): o painel
 * mostra o corpo enviado como JSON, sem interpretá-lo.
 *
 * Contrato completo: `docs/webhooks-atendimentos.md`.
 */
export const WEBHOOK_EVENTS = {
  ticketCreated: 'atendimento.criado',
  ticketUpdated: 'atendimento.atualizado',
  ticketClosed: 'atendimento.encerrado',
  interactionCreated: 'interacao.criada',
  /** Áudio transcrito depois: o corpo da mesma interação passa a ter texto. */
  interactionUpdated: 'interacao.atualizada',
  /** Disparado à mão pelo painel para conferir URL, segredo e rota. */
  ping: 'ping',
} as const;

export type WebhookEventName = (typeof WEBHOOK_EVENTS)[keyof typeof WEBHOOK_EVENTS];

export const WEBHOOK_EVENT_NAMES = Object.values(WEBHOOK_EVENTS) as WebhookEventName[];

/**
 * `discarded` é diferente de `failed`: o receptor respondeu 4xx, recusando o
 * conteúdo. Insistir daria o mesmo resultado, então a entrega espera correção do
 * outro lado e reenvio manual.
 */
export type WebhookDeliveryStatus = 'pending' | 'delivered' | 'failed' | 'discarded';

export type WebhookResourceType = 'conversation' | 'message' | 'system';

export interface WebhookDelivery {
  id: string;
  event: string;
  status: WebhookDeliveryStatus;
  resourceType: WebhookResourceType;
  resourceId: string | null;
  conversationId: string | null;
  endpointUrl: string;
  attempts: number;
  responseStatus: number | null;
  responseBody: string | null;
  lastError: string | null;
  deliveredAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WebhookDeliveryDetail extends WebhookDelivery {
  /** Corpo exato que foi assinado e enviado — não uma remontagem. */
  payload: Record<string, unknown>;
}

/** Configuração da integração. Nunca inclui o segredo, só se existe um. */
export interface WebhookIntegration {
  enabled: boolean;
  url: string | null;
  hasSecret: boolean;
  events: string[];
  timeoutMs: number;
  maxAttempts: number;
  ticketIdPath: string | null;
  retentionDays: number;
}

export interface WebhookDeliveryStats {
  total: number;
  pending: number;
  delivered: number;
  failed: number;
  discarded: number;
  lastDeliveredAt: string | null;
  /** Última vez que uma entrega parou de tentar — `failed` ou `discarded`. */
  lastFailureAt: string | null;
}

// ─────────────────────────────────────────────────────────────
// Eventos de WebSocket (servidor -> cliente)
// ─────────────────────────────────────────────────────────────

export const WS_EVENTS = {
  messageNew: 'message.new',
  /** Uma mensagem já exibida mudou — hoje, áudio que acabou de ser transcrito. */
  messageUpdated: 'message.updated',
  conversationUpdated: 'conversation.updated',
  /** O ticket deixou de existir; quem o tem na tela precisa tirá-lo de lá. */
  conversationDeleted: 'conversation.deleted',
  whatsappStatus: 'whatsapp.status',
  whatsappQr: 'whatsapp.qr',
  /** Chamada entrante detectada pelo servidor (webhook), antes ou junto do SDK. */
  callIncoming: 'call.incoming',
  callUpdated: 'call.updated',
  agentRun: 'agent.run',
  importProgress: 'import.progress',
  knowledgeUpdated: 'knowledge.updated',
} as const;

export type WsEventName = (typeof WS_EVENTS)[keyof typeof WS_EVENTS];

export interface WsEventPayloads {
  [WS_EVENTS.messageNew]: { conversationId: string; message: Message };
  [WS_EVENTS.messageUpdated]: { conversationId: string; message: Message };
  [WS_EVENTS.conversationUpdated]: { conversation: Conversation };
  [WS_EVENTS.conversationDeleted]: { conversationId: string; ticketNumber: number };
  [WS_EVENTS.whatsappStatus]: { session: WhatsappSession };
  [WS_EVENTS.whatsappQr]: { sessionId: string; qrCode: string };
  [WS_EVENTS.callIncoming]: { call: Call };
  [WS_EVENTS.callUpdated]: { call: Call };
  [WS_EVENTS.agentRun]: { run: AgentRun };
  [WS_EVENTS.importProgress]: { job: CsvImportJob };
  [WS_EVENTS.knowledgeUpdated]: { documentId: string; embeddingStatus: EmbeddingStatus };
}

// ─────────────────────────────────────────────────────────────
// Utilidades de paginação
// ─────────────────────────────────────────────────────────────

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export const DEFAULT_PAGE_SIZE = 30;

// ─────────────────────────────────────────────────────────────
// Tickets de suporte técnico (kanban)
// ─────────────────────────────────────────────────────────────

/**
 * Coluna do kanban.
 *
 * Não é um campo do banco: é derivado de `status` + `handler`. Guardar a coluna
 * à parte criaria um segundo eixo de verdade — um card em "Em atendimento" com
 * a conversa ainda em mãos do robô — e o painel passaria a mentir sempre que
 * uma escalada acontecesse fora do kanban. Arrastar um card executa a ação real
 * (assumir, devolver para a fila, encerrar, reabrir); a coluna é a consequência.
 */
export type TicketStage = 'novo' | 'fila' | 'atendimento' | 'resolvido';

export const TICKET_STAGES: Array<{
  id: TicketStage;
  label: string;
  hint: string;
}> = [
  { id: 'novo', label: 'Novos', hint: 'Triagem do assistente, sem atendente designado' },
  { id: 'fila', label: 'Na fila', hint: 'Esperando um atendente assumir' },
  { id: 'atendimento', label: 'Em atendimento', hint: 'Alguém está respondendo agora' },
  { id: 'resolvido', label: 'Resolvido', hint: 'Encerrado — volta a abrir se o cliente escrever' },
];

export function ticketStage(conversation: Pick<Conversation, 'status' | 'handler'>): TicketStage {
  if (conversation.status === 'closed') return 'resolvido';
  if (conversation.status === 'pending_human') return 'fila';
  return conversation.handler === 'human' ? 'atendimento' : 'novo';
}

export interface TicketBoardColumn {
  stage: TicketStage;
  /** Total na coluna; pode ser maior que `cards.length` em "Resolvido". */
  total: number;
  cards: Conversation[];
}

export interface TicketBoard {
  columns: TicketBoardColumn[];
  /** Quantos cards a coluna "Resolvido" traz por vez. */
  resolvedLimit: number;
}

// ─────────────────────────────────────────────────────────────
// Monitor de atendimentos ao vivo
// ─────────────────────────────────────────────────────────────

/** Um card do monitor: a conversa e o fim da conversa dela. */
export interface LiveConversation {
  conversation: Conversation;
  messages: Message[];
}

/** Quantas mensagens cada card do monitor carrega no primeiro desenho. */
export const LIVE_MESSAGE_WINDOW = 8;

// ─────────────────────────────────────────────────────────────
// Configurações de atendimento humano
// ─────────────────────────────────────────────────────────────

/**
 * Assinatura que identifica quem está do outro lado.
 *
 * Existe porque o cliente vê um número só: sem isso, robô e três atendentes
 * diferentes falam com a mesma voz. Fica por empresa (e não por atendente) para
 * o setor ser escrito uma vez e valer para todo mundo.
 */
export interface SupportSettings {
  signatureEnabled: boolean;
  signatureDepartment: string;
  /** Aceita `{nome}` e `{setor}`. */
  signatureTemplate: string;
  updatedAt: string;
}

/**
 * O `*` é do próprio WhatsApp e fica **dentro** do modelo, não em volta dele.
 *
 * É o que permite marcar só o nome — que é o que muda de pessoa para pessoa e o
 * que o cliente precisa reconhecer — deixando o setor em texto normal. Modelo com
 * negrito por fora obrigaria a escolher entre marcar tudo ou nada.
 */
export const DEFAULT_SIGNATURE_TEMPLATE = '*{nome}* - {setor}';
export const DEFAULT_SIGNATURE_DEPARTMENT = 'Suporte';

/** Preenche o modelo. Devolve vazio quando não sobra nada legível. */
export function renderSignature(
  template: string,
  values: { nome: string; setor: string },
): string {
  return (
    template
      .replaceAll('{nome}', values.nome.trim())
      .replaceAll('{setor}', values.setor.trim())
      // Campo vazio deixa marcação órfã (`**`) e separador solto ("- Suporte",
      // "Angelo -"); limpar aqui evita validar cada combinação de modelo.
      .replace(/\*\s*\*/g, '')
      .replace(/^[\s\-—·|:]+|[\s\-—·|:]+$/g, '')
      .trim()
  );
}

/**
 * Abre a mensagem com a assinatura e manda o texto para a linha de baixo:
 * `*Angelo* - Suporte:\nbom dia`.
 *
 * Em linha própria porque a resposta raramente é uma frase curta: colada no
 * mesmo parágrafo, a identificação empurra o começo do texto para o meio da
 * bolha e o cliente lê tudo como um bloco só. Separada, o nome em negrito
 * funciona como cabeçalho e a resposta começa onde o olho espera.
 *
 * O texto guardado no histórico continua sendo o que o atendente escreveu — a
 * assinatura entra só no que sai para o WhatsApp. No painel, quem falou já está
 * escrito na bolha, e repetir isso em toda mensagem só ocuparia a tela.
 */
export function applySignature(content: string, signature: string): string {
  const trimmed = content.trim();
  if (!signature) return trimmed;
  return `${signature}:\n${trimmed}`;
}

// ─────────────────────────────────────────────────────────────
// Fotos de perfil
// ─────────────────────────────────────────────────────────────

/**
 * Foto de perfil, de quem quer que seja.
 *
 * São duas origens com o mesmo destino na tela: a do cliente vem do WhatsApp e é
 * copiada para cá pelo Baileys; a do atendente é enviada por ele mesmo no painel.
 * O `kind` está na URL (`/api/avatars/contact/:id`, `/api/avatars/user/:id`)
 * porque a busca é por empresa em tabelas diferentes — e um id de contato nunca
 * deve alcançar a foto de um usuário.
 */
export type AvatarKind = 'contact' | 'user';

/**
 * 2 MB. Foto de perfil é exibida em 40 pixels; o que passa disso é peso que
 * ninguém vê — e como não redimensionamos no servidor, o limite é a única defesa
 * contra um retrato de 20 MB sendo baixado a cada abertura do painel.
 */
export const AVATAR_MAX_BYTES = 2 * 1024 * 1024;

/**
 * O que aceitamos receber. A lista é fechada porque o tipo é decidido pelos
 * bytes do arquivo, não pelo que o navegador declara: SVG, por exemplo, é
 * documento com script, não imagem.
 */
export const AVATAR_ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

/**
 * Versão da foto de cada atendente da empresa que tem foto.
 *
 * O painel precisa disso para as bolhas: a mensagem carrega quem escreveu, mas não
 * quando aquela pessoa trocou a foto — e sem versão a imagem antiga ficaria em
 * cache. Uma lista só, carregada uma vez, resolve para todas as conversas; pedir a
 * versão mensagem por mensagem seria dezenas de consultas para os mesmos cinco
 * atendentes.
 */
export interface UserAvatarVersion {
  userId: string;
  avatarUpdatedAt: string;
}

export interface AvatarState {
  /** `null` quando não há foto — o painel cai nas iniciais. */
  avatarUpdatedAt: string | null;
}

/**
 * Iniciais para quando não há foto: uma ou duas letras, do primeiro e do último
 * nome.
 *
 * O fallback não é enfeite. A maior parte dos contatos de WhatsApp esconde a foto
 * de quem não está na agenda, então esta é a aparência normal de metade da lista —
 * e um retângulo cinza vazio não diferencia uma linha da outra.
 */
export function initialsFor(name: string | null | undefined): string {
  const parts = (name ?? '')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();

  return `${parts[0]![0]!}${parts[parts.length - 1]![0]!}`.toUpperCase();
}

// ─────────────────────────────────────────────────────────────
// Anexos enviados pelo painel
// ─────────────────────────────────────────────────────────────

/**
 * O que o atendente pode mandar para o cliente além de texto e voz.
 *
 * São três e não um "arquivo" genérico porque o WhatsApp trata cada um de forma
 * diferente na tela do cliente: foto e vídeo aparecem abertos na conversa, com
 * legenda embaixo; documento aparece como cartão com nome e tamanho, para baixar.
 * Mandar um PDF como imagem, ou uma foto como documento, chega errado do outro
 * lado — então o tipo é decidido aqui e viaja até o envio.
 */
export type AttachmentKind = 'image' | 'video' | 'document';

/**
 * 32 MB por anexo.
 *
 * É o teto que o próprio WhatsApp aplica na mídia de conversa: acima disso o
 * envio é recusado na entrega, depois de o arquivo já ter subido e a bolha já ter
 * aparecido no painel — a pior ordem possível para o atendente descobrir. Cortar
 * antes, no navegador, transforma isso em um aviso imediato.
 *
 * Não é o `MEDIA_MAX_MB` do servidor: aquele governa o que aceitamos **receber**
 * do WhatsApp, que é limite nosso de disco, não do protocolo.
 */
export const ATTACHMENT_MAX_BYTES = 32 * 1024 * 1024;

/** Limite de legenda do WhatsApp. Acima disso o texto é cortado na entrega. */
export const ATTACHMENT_CAPTION_MAX_LENGTH = 1024;

/**
 * Fotos. GIF entra aqui e chega como imagem parada — o WhatsApp só anima GIF que
 * vem convertido em vídeo. Perder o movimento é melhor que recusar o arquivo.
 */
export const ATTACHMENT_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
] as const;

/**
 * Vídeos que o cliente consegue assistir dentro da conversa.
 *
 * A lista é curta de propósito: são os três contêineres da família MP4 (mp4, 3gp,
 * mov), os únicos que o WhatsApp toca sem reconverter. WebM e Matroska são vídeo
 * de verdade, mas chegariam como bolha que não abre — por isso vão como documento,
 * onde ao menos o cliente salva o arquivo e assiste no computador dele.
 */
export const ATTACHMENT_VIDEO_MIME_TYPES = [
  'video/mp4',
  'video/3gpp',
  'video/quicktime',
] as const;

/**
 * O que **não** sai como documento — o resto sai.
 *
 * Documento é lista aberta: ZIP, JSON, XML de nota fiscal, DWG, SQL, o instalador
 * do driver... o atendimento manda o que o cliente pediu, e uma lista fechada de
 * tipos virava "formato não aceito" para arquivo legítimo toda semana — o `.zip`
 * do Windows, que chega como `application/x-zip-compressed`, foi o primeiro.
 *
 * Fora ficam só duas famílias, por motivos diferentes:
 *
 * - **Página e script** (HTML, XHTML, SVG, JavaScript): o painel baixa a mídia
 *   por `fetch` e a exibe por object URL, que vive no domínio do painel. Um
 *   HTML aberto dali executa com o token do atendente na aba. O servidor ainda
 *   entrega documento como download forçado, mas a defesa não fica em uma camada.
 * - **Executável** (EXE, MSI, script de shell): não há ameaça ao painel, mas
 *   ninguém precisa mandar `.exe` a um cliente por um canal de suporte, e o
 *   antivírus do outro lado apagaria antes de abrir.
 *
 * XML fica de fora da lista de bloqueio de propósito: o XML da nota fiscal é um
 * dos arquivos mais pedidos em suporte no Brasil. Ele só é inofensivo porque
 * documento nunca é servido com o tipo original — ver `MediaController`.
 */
export const ATTACHMENT_BLOCKED_MIME_TYPES = [
  'text/html',
  'application/xhtml+xml',
  'image/svg+xml',
  'text/javascript',
  'application/javascript',
  'application/x-javascript',
  'text/ecmascript',
  'application/ecmascript',
  'application/x-msdownload',
  'application/x-msdos-program',
  'application/x-ms-installer',
  'application/x-msi',
  'application/vnd.microsoft.portable-executable',
  'application/x-executable',
  'application/x-sh',
  'application/x-bat',
  'application/x-shellscript',
] as const;

/**
 * Nomes diferentes para o mesmo tipo, reduzidos ao canônico.
 *
 * Cada sistema operacional rotula a mesma extensão do seu jeito: `.zip` é
 * `application/zip` no Linux e `application/x-zip-compressed` no Windows; `.rar`
 * tem três nomes; `.m4a` dois. O canônico é o que vai para o disco, para o
 * `Content-Type` e para o WhatsApp.
 */
const ATTACHMENT_MIME_ALIASES: Record<string, string> = {
  'application/x-zip-compressed': 'application/zip',
  'application/x-zip': 'application/zip',
  'application/vnd.rar': 'application/x-rar-compressed',
  'application/x-rar': 'application/x-rar-compressed',
  'text/json': 'application/json',
  'text/xml': 'application/xml',
  'audio/mp3': 'audio/mpeg',
  'audio/x-m4a': 'audio/mp4',
  'audio/x-mpeg': 'audio/mpeg',
  'image/jpg': 'image/jpeg',
  'image/pjpeg': 'image/jpeg',
  'video/x-m4v': 'video/mp4',
};

/**
 * `image/jpeg; charset=binary`, `IMAGE/JPEG` e `image/jpg` são o mesmo tipo.
 * Devolve `null` para tipo em branco.
 */
export function normalizeAttachmentMimeType(mimeType: string | null | undefined): string | null {
  if (!mimeType) return null;
  const base = mimeType.split(';')[0]!.trim().toLowerCase();
  if (!base) return null;
  return ATTACHMENT_MIME_ALIASES[base] ?? base;
}

/**
 * Extensão -> tipo, para quando o navegador não sabe dizer — e, ao contrário,
 * para dar extensão ao arquivo que chegou sem nome.
 *
 * Acontece de verdade: `.csv` no Windows sem Excel instalado, `.mkv`, e todo
 * arquivo arrastado de um compartilhamento de rede chegam como
 * `application/octet-stream` ou com o tipo em branco.
 */
export const ATTACHMENT_MIME_BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  '3gp': 'video/3gpp',
  mov: 'video/quicktime',
  webm: 'video/webm',
  mkv: 'video/x-matroska',
  avi: 'video/x-msvideo',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  ogg: 'audio/ogg',
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  odt: 'application/vnd.oasis.opendocument.text',
  ods: 'application/vnd.oasis.opendocument.spreadsheet',
  zip: 'application/zip',
  rar: 'application/x-rar-compressed',
  '7z': 'application/x-7z-compressed',
  json: 'application/json',
  xml: 'application/xml',
  txt: 'text/plain',
  log: 'text/plain',
  csv: 'text/csv',
};

const EXTENSION_BY_MIME: Record<string, string> = Object.entries(
  ATTACHMENT_MIME_BY_EXTENSION,
).reduce<Record<string, string>>((acc, [extension, mimeType]) => {
  acc[mimeType] ??= extension;
  return acc;
}, {});

/** Extensão do tipo (`pdf` para `application/pdf`), ou `null` quando não conhecemos. */
export function extensionForMimeType(mimeType: string | null | undefined): string | null {
  return EXTENSION_BY_MIME[normalizeAttachmentMimeType(mimeType) ?? ''] ?? null;
}

/**
 * Nome com que o arquivo é salvo no computador do atendente.
 *
 * No disco o arquivo é o id da mensagem e o servidor entrega documento como
 * `application/octet-stream` — então é só pelo nome que o sistema operacional sabe
 * com que programa abrir. Sem extensão, o atendente recebia um arquivo "sem
 * formato" que o Windows não abre.
 */
export function attachmentDownloadName(
  fileName: string | null | undefined,
  mimeType: string | null | undefined,
  fallbackStem: string,
): string {
  const name = fileName?.trim() || fallbackStem;
  if (/\.[a-z0-9]{1,8}$/i.test(name)) return name;

  const extension = extensionForMimeType(mimeType);
  return extension ? `${name}.${extension}` : name;
}

/**
 * Como o anexo deve ir para o WhatsApp — `null` para o que não aceitamos e
 * para tipo em branco (aí quem decide é a extensão, no servidor).
 *
 * Fica no pacote compartilhado porque as duas pontas precisam da mesma resposta:
 * o painel decide o que recusar antes de subir 30 MB, e o servidor decide qual
 * mensagem montar. Duas listas separadas divergiriam, e a divergência apareceria
 * como "o painel deixou escolher e o servidor recusou".
 */
export function attachmentKindFor(mimeType: string | null | undefined): AttachmentKind | null {
  const base = normalizeAttachmentMimeType(mimeType);
  if (!base) return null;

  if ((ATTACHMENT_IMAGE_MIME_TYPES as readonly string[]).includes(base)) return 'image';
  if ((ATTACHMENT_VIDEO_MIME_TYPES as readonly string[]).includes(base)) return 'video';
  if ((ATTACHMENT_BLOCKED_MIME_TYPES as readonly string[]).includes(base)) return null;

  return 'document';
}
